import { Autocomplete, FormControl, TextField } from '@mui/material';
import { type FC, startTransition, useCallback, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { drawerZIndex } from '../../components/filter/FilterDrawer';
import { FilterSelectedLabel } from '../../components/filter/filterUtils';
import { type HealthLevel, healthLevel } from '../../dtos';

export const levelFilterKey = 'level';

type LevelFilterProps = {
  readonly inDrawer?: boolean;
};

export const levelOptions: { value: HealthLevel; label: string }[] = [
  { value: 'error', label: 'Fehler' },
  { value: 'warning', label: 'Warnung' },
];

export function initializeLevelParam(params: URLSearchParams) {
  const initialValue = z.array(z.enum(healthLevel)).parse(JSON.parse(params.get(levelFilterKey) ?? '[]'));
  if (initialValue.length === 0) {
    params.set(levelFilterKey, JSON.stringify([healthLevel.ERROR]));
  }
}

export function useLevelFilter() {
  const [searchParams, setSearchParams] = useSearchParams();

  const actualValue = z.array(z.enum(healthLevel)).parse(JSON.parse(searchParams.get(levelFilterKey) ?? '[]'));

  const [optimisticLevelFilter, setOptimisticLevelFilter] = useOptimistic(
    actualValue,
    (_, newValue: HealthLevel[]) => newValue,
  );

  const setLevelFilter = useCallback(
    (value: HealthLevel[]) => {
      startTransition(() => {
        setOptimisticLevelFilter(value);
        setSearchParams(
          // The prev parameter from react-router may be stale, see https://github.com/remix-run/react-router/issues/9757
          () => {
            const prev = new URLSearchParams(window.location.search);
            if (value.length === 0) {
              prev.delete(levelFilterKey);
            } else {
              prev.set(levelFilterKey, JSON.stringify(value));
            }
            return prev;
          },
          { replace: true },
        );
      });
    },
    [setSearchParams, setOptimisticLevelFilter],
  );

  return [optimisticLevelFilter, setLevelFilter] as const;
}

export const LevelFilter: FC<LevelFilterProps> = ({ inDrawer }: LevelFilterProps) => {
  const [levelFilter, setLevelFilter] = useLevelFilter();

  return (
    <FormControl sx={{ width: inDrawer ? '100%' : '12rem' }}>
      <Autocomplete
        id="level-input"
        size="small"
        multiple
        value={levelOptions.filter((option) => levelFilter.includes(option.value))}
        onChange={(_, newValues) => {
          setLevelFilter(newValues.map((option) => option.value));
        }}
        options={levelOptions}
        limitTags={0}
        slotProps={{ popper: { sx: { zIndex: (theme) => drawerZIndex(theme) + 1 } } }}
        getLimitTagsText={FilterSelectedLabel}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Level"
            sx={{
              '& input': {
                position: () => (levelFilter.length > 0 ? 'absolute' : undefined),
              },
            }}
          />
        )}
      />
    </FormControl>
  );
};
